import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ghost, Loader2, Upload, Download, Sparkles, RotateCcw, ImageDown, Users } from "lucide-react";

const STYLES = [
  { v: "Classic Survivor", d: "Gritty fog-walker" },
  { v: "Wounded & Hooked", d: "Just escaped" },
  { v: "The Killer", d: "Become the threat" },
  { v: "Spirit / Ghostly", d: "Supernatural" },
];

// Downscale + compress a selfie client-side before upload.
const fileToResizedDataUrl = (file, max = 768) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > max) { height = Math.round((height * max) / width); width = max; }
        else if (height > max) { width = Math.round((width * max) / height); height = max; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function SurvivorMaker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [selfie, setSelfie] = useState(null);
  const [style, setStyle] = useState("Classic Survivor");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      setSelfie(dataUrl);
      setResult(null);
    } catch {
      toast.error("Could not read that image.");
    }
  };

  const generate = async () => {
    if (!user) return setAuthOpen(true);
    if (!selfie) return toast.error("Upload a selfie first.");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/generate/survivor", { image_base64: selfie, style });
      setResult(data.image);
      setVersion((v) => v + 1);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error(e?.response?.data?.detail || "Generation failed, please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = result;
    a.download = "my-dbd-survivor.png";
    a.click();
  };

  const postToCommunity = async () => {
    setPosting(true);
    try {
      await api.post("/survivor/share", { image_base64: result, style, caption });
      toast.success("Posted to the community!", { action: { label: "View", onClick: () => navigate("/community") } });
      setShareOpen(false);
      setCaption("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div data-testid="survivor-maker-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Ghost className="h-5 w-5" />
        <span className="text-xs font-bold uppercase tracking-[0.25em]">Survivor Maker</span>
      </div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">See yourself in <span className="text-primary text-glow-red">The Fog</span></h1>
      <p className="mt-3 max-w-2xl text-foreground/60">Upload a selfie and our AI reimagines you as a Dead by Daylight survivor — perfect for thumbnails, avatars and channel art.</p>

      <div className="mt-8"><UpgradeBanner version={version} /></div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* INPUT */}
        <div className="border border-border bg-card p-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">1 · Your Selfie</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="selfie-input" />
          <button
            data-testid="selfie-dropzone"
            onClick={() => fileRef.current?.click()}
            className="flex aspect-square w-full items-center justify-center overflow-hidden border border-dashed border-white/15 bg-background/50 transition-colors hover:border-primary/50"
          >
            {selfie ? (
              <img src={selfie} alt="Your selfie" className="h-full w-full object-cover" />
            ) : (
              <div className="text-center text-foreground/40">
                <Upload className="mx-auto h-8 w-8" />
                <p className="mt-2 text-sm">Click to upload a clear, well-lit selfie</p>
              </div>
            )}
          </button>

          <p className="mb-3 mt-6 text-xs font-bold uppercase tracking-widest text-foreground/70">2 · Pick a Look</p>
          <div className="grid grid-cols-2 gap-2.5">
            {STYLES.map((s) => (
              <button
                key={s.v}
                data-testid={`style-${s.v.replace(/\W/g, "-")}`}
                onClick={() => setStyle(s.v)}
                className={`border p-3 text-left transition-all ${style === s.v ? "border-primary bg-primary/10" : "border-white/10 hover:border-primary/40"}`}
              >
                <p className="text-sm font-bold">{s.v}</p>
                <p className="text-xs text-foreground/50">{s.d}</p>
              </button>
            ))}
          </div>

          <Button data-testid="generate-survivor-btn" onClick={generate} disabled={loading || !selfie} className="mt-6 w-full rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90">
            {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Entering the fog…</> : <><Sparkles className="mr-2 h-5 w-5" /> Transform Me</>}
          </Button>
        </div>

        {/* OUTPUT */}
        <div className="border border-border bg-card p-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Result</p>
          <div className="flex aspect-square w-full items-center justify-center overflow-hidden border border-white/10 bg-background/50">
            {loading ? (
              <div className="text-center text-foreground/40">
                <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                <p className="mt-3 text-sm">Conjuring your survivor… (15–30s)</p>
              </div>
            ) : result ? (
              <img data-testid="survivor-result" src={result} alt="Your DBD survivor" className="h-full w-full object-cover fade-up" />
            ) : (
              <div className="text-center text-foreground/30">
                <ImageDown className="mx-auto h-10 w-10" />
                <p className="mt-2 text-sm">Your transformation appears here</p>
              </div>
            )}
          </div>

          {result && (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button data-testid="download-survivor-btn" onClick={download} className="rounded-sm bg-primary uppercase tracking-widest hover:bg-primary/90">
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button data-testid="post-survivor-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-sm border-primary/50 bg-transparent uppercase tracking-widest hover:bg-primary/10">
                <Users className="mr-2 h-4 w-4" /> Post to Community
              </Button>
              <Button onClick={generate} variant="outline" className="rounded-sm border-white/10 bg-transparent uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
                <RotateCcw className="mr-2 h-4 w-4" /> Regenerate
              </Button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-foreground/35">Selfies are processed only to generate your image and are not stored. AI results are stylised and may vary.</p>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md rounded-sm border-white/10 bg-card">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-3xl tracking-wider"><Users className="h-5 w-5 text-primary" /> Post to Community</DialogTitle></DialogHeader>
          {result && <img src={result} alt="Preview" className="aspect-square w-full border border-white/10 object-cover" />}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-foreground/70">Caption (optional)</Label>
            <Input data-testid="survivor-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="How do I look in the fog?" className="rounded-sm border-white/10 bg-background" />
          </div>
          <Button data-testid="survivor-post-submit" onClick={postToCommunity} disabled={posting} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest hover:bg-primary/90">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Share with the Fog"}
          </Button>
        </DialogContent>
      </Dialog>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
